import { DeviceEventHit } from '@eppendorf/vnls-device-service-utils';
import { Event as DeviceEvent } from '@eppendorf/vnls-notifications-utils';
import { addDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isFeatureEnabled } from '$shared/feature-toggle/is-feature-enabled';

import {
  useDeviceEventsWithQueryParamsV2,
  useLatestDeviceEventsWithQueryParamsV2,
} from '$features/monitoring/events/events-v2.api';
import {
  useDeviceEventsWithQueryParams,
  useLatestDeviceEventsWithQueryParams,
} from '$features/monitoring/events/events.api';

const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_FROM_DATE = addDays(new Date(), -90).getTime().toString();

function isSameEvent(
  left: DeviceEvent | DeviceEventHit,
  right: DeviceEvent | DeviceEventHit,
) {
  return left.timestamp === right.timestamp && left.message === right.message;
}

export const useRequestMonitoringEvents = () => {
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState<(DeviceEvent | DeviceEventHit)[]>([]);
  const [page, setPage] = useState(DEFAULT_PAGE_NUMBER);
  const [deviceHasNewEvents, setDeviceHasNewEvents] = useState(false);
  const queryParams = {
    serialNumber: searchParams.get('serialNumber') ?? '',
    tags: searchParams.get('tags')?.split(',') || undefined,
    from: searchParams.get('from') ?? DEFAULT_FROM_DATE,
    to: searchParams.get('to') ?? undefined,
    pageSize: DEFAULT_PAGE_SIZE,
    message: searchParams.get('message') || undefined,
    page,
  };

  const useV2 = isFeatureEnabled('ESEVNLS_4552_DEVICE_EVENT_CATEGORIES');

  const useInitial = useV2
    ? useDeviceEventsWithQueryParamsV2
    : useDeviceEventsWithQueryParams;
  const useLatest = useV2
    ? useLatestDeviceEventsWithQueryParamsV2
    : useLatestDeviceEventsWithQueryParams;

  const { data, ...requestStatus } = useInitial(queryParams);
  const { data: latestEvents, isPlaceholderData: isLatestEventsQueryPlaceholder } =
    useLatest(queryParams);

  const updateFirstPageEvents = useCallback(() => {
    if (latestEvents) {
      setEvents((prev) => {
        const newEvents = latestEvents.filter(
          (latestEvent) => !prev.some((event) => isSameEvent(event, latestEvent)),
        );
        return [...newEvents, ...prev].slice(0, DEFAULT_PAGE_SIZE);
      });
    }
  }, [latestEvents]);

  useEffect(() => {
    setPage(DEFAULT_PAGE_NUMBER);
    setDeviceHasNewEvents(false);
    setEvents([]);
  }, [queryParams.message, queryParams.from, queryParams.to]);

  useEffect(() => {
    if (data?.length) {
      setEvents((prev) => {
        const newEvents = data.filter(
          (event) => !prev.some((existingEvent) => isSameEvent(existingEvent, event)),
        );
        return [...prev, ...newEvents];
      });
    }
  }, [data]);

  useEffect(() => {
    if (latestEvents?.length && !deviceHasNewEvents && !isLatestEventsQueryPlaceholder) {
      const hasNewEvents = latestEvents.some(
        (event) => !(events ?? []).some((e) => isSameEvent(e, event)),
      );
      setDeviceHasNewEvents(hasNewEvents);
    }
  }, [latestEvents, deviceHasNewEvents, events]);

  const loadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const resetNewEventsTracker = useCallback(() => {
    setDeviceHasNewEvents(false);
  }, []);

  return {
    events,
    loadMore,
    requestStatus,
    deviceHasNewEvents,
    resetNewEventsTracker,
    updateFirstPage: updateFirstPageEvents,
  };
};
